/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef} from 'react'
import {useParams} from 'react-router'
import {
    Stack,
    Box,
    SimpleGrid,
    Spinner
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

import {useOrderDetail} from '../../hooks/use-order-detail'
import {AbsoluteCenter} from '@chakra-ui/react'
import {OrderTracking, ReturnOrderPolicyAlert, ReturnAlert} from './partials'
import {
    OrderHeading,
    OrderStatus,
    OrderProducts,
    OrderPayment,
    ManageOrder,
    OrderDetailsSummary,
    StoreOrderDetails
} from './partials'
const onClient = typeof window !== 'undefined'

// CONSTANTS
import {ORDER_RAW_STATUS, ORDER_PRODUCTS_RENDER_MODES} from './constant'

const AccountOrderDetail = () => {
    const {orderId} = useParams()
    const {currency} = useCurrency()

    const {data: {order = {}} = {}, isLoading: isOrderLoading} = useOrderDetail(orderId, {
        enabled: onClient && !!orderId
    })
    const isLoading = false
    const shouldShowTracking = (order?.packages?.length > 0) && ORDER_RAW_STATUS.TRACKING.includes(order.statusRaw?.toLowerCase())

    const headingRef = useRef()
    useEffect(() => {
        // Focus the 'Order Details' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    const getOrderProductsMode = (order) =>
        ORDER_RAW_STATUS.TRACKING.includes(order.statusRaw?.toLowerCase()) &&
        order.packages?.length > 0
            ? ORDER_PRODUCTS_RENDER_MODES.PACKAGES
            : ORDER_PRODUCTS_RENDER_MODES.ITEMS

    const mode = getOrderProductsMode(order)
    const orderProductsData = mode === ORDER_PRODUCTS_RENDER_MODES.PACKAGES ? order : order.items

    if (isOrderLoading) {
        return (
            // eslint-disable-next-line prettier/prettier
            <AbsoluteCenter>
                <Spinner />
            </AbsoluteCenter>
        )
    }

    if (!order.isOnlineOrder) {
        return <StoreOrderDetails order={order} headingRef={headingRef} currency={currency}/>
    }

    return (
        <Stack spacing={6} data-testid="account-order-details-page">
            <SimpleGrid columns={{base: 1, lg: 2}} spacing={{base: 6, lg: '120px'}}>
                <Box>
                    <Stack>
                        <OrderHeading order={order} headingRef={headingRef} isLoading={isLoading} />
                        {/* Desktop only - return alerts */}
                        <Box display={{base: 'none', lg: 'block'}}>
                            <ReturnAlert orderItems={order.items}/>
                            <ReturnOrderPolicyAlert
                                {...order.returnableStatus}
                                containerProps={{mb: 6}}
                            />
                        </Box>
                        {shouldShowTracking ? (
                            <OrderTracking order={order} />
                        ) : (
                            <OrderStatus order={order} />
                        )}
                        {/* Mobile only - manage order section */}
                        <Box display={{base: 'block', lg: 'none'}}>
                            <ManageOrder
                                containerProps={{mt: 2}}
                                dividerProps={{mb: 4}}
                                {...order.returnableStatus}
                                returnLabel={order.returnLabel}
                            />
                        </Box>
                        <OrderProducts
                            orderBrand={order.orderBrand}
                            orderProductsData={orderProductsData}
                            currency={currency}
                            mode={mode}
                        />
                        <OrderPayment order={order} />
                    </Stack>
                </Box>
                <Box maxW={{base: 'full', lg: '370px'}}>
                    <Box display={{base: 'none', lg: 'block'}}>
                        <ManageOrder {...order.returnableStatus} returnLabel={order.returnLabel} />
                    </Box>
                    <OrderDetailsSummary {...order} currency={currency} />
                </Box>
            </SimpleGrid>
        </Stack>
    )
}

AccountOrderDetail.getTemplateName = () => 'account-order-history'

export default AccountOrderDetail
